import { inject, Injectable } from '@angular/core';

import { Observable, Subject, take } from 'rxjs';

import {
  SkillsIdeateFlowData,
  SkillsIdeateResultJson,
  SkillsIdeateStep,
  SkillsIdeateStepDefinition,
  SkillsIdeateStepI,
} from '@core/tellent-skills-types';
import { DialogRef, DialogService } from '@core/user-interface-legacy';

import { skillsIdeateStepComponentMap } from './skills-ideate-flow-component.map';
import { SkillsIdeateFlowStateService } from './skills-ideate-flow-state.service';

@Injectable({ providedIn: 'root' })
export class SkillsIdeateFlowFacade {
  private readonly dialogService = inject(DialogService);
  private readonly flowStateService = inject(SkillsIdeateFlowStateService);
  private readonly stepComponentMap: Record<SkillsIdeateStep, SkillsIdeateStepI> =
    skillsIdeateStepComponentMap;

  private activeDialogRef?: DialogRef;
  private flowCompletion$?: Subject<SkillsIdeateResultJson | undefined>;

  public startFlow(): void {
    this.beginFlow();
  }

  public startFlowAsTellentClientCompatibleStream(): Observable<
    SkillsIdeateResultJson | undefined
  > {
    const completion$ = new Subject<SkillsIdeateResultJson | undefined>();
    this.beginFlow(completion$);
    return completion$.asObservable();
  }

  public cancelFlow(): void {
    if (this.activeDialogRef) {
      this.activeDialogRef.close();
      this.activeDialogRef = undefined;
    }
    if (this.flowCompletion$ && !this.flowCompletion$.closed) {
      this.flowCompletion$.next(undefined);
      this.flowCompletion$.complete();
      this.flowCompletion$ = undefined;
    }
  }

  private beginFlow(completion$?: Subject<SkillsIdeateResultJson | undefined>): void {
    this.cancelFlow();
    this.flowCompletion$ = completion$;
    const firstStep = this.flowStateService.initializeFlow();
    if (firstStep) {
      this.openStepDialog(firstStep);
    } else {
      this.finishFlow();
    }
  }

  private openStepDialog(stepDef: SkillsIdeateStepDefinition): void {
    const stepToOpen = this.stepComponentMap[stepDef.step];

    if (!stepToOpen) {
      console.error(`No component registered for step: ${stepDef.step}`);
      this.finishFlow();
      return;
    }

    this.flowStateService.flowData$.pipe(take(1)).subscribe(data => {
      this.activeDialogRef = this.dialogService.openDialog(
        stepToOpen.dialogComponent,
        { ...data, ...stepDef },
        stepToOpen.dialogConfig,
      );

      this.activeDialogRef.afterClosed.pipe(take(1)).subscribe(result => {
        if (result === undefined) {
          this.activeDialogRef = undefined;
          this.finishFlow(data);
          return;
        }

        if (result?.back) {
          const prevStep = this.flowStateService.goBack();
          if (prevStep) {
            this.openStepDialog(prevStep);
          } else {
            this.activeDialogRef = undefined;
            this.finishFlow(data);
          }
          return;
        }

        const nextStep = this.flowStateService.advanceFlow(result);
        if (nextStep) {
          this.openStepDialog(nextStep);
        } else {
          this.activeDialogRef = undefined;
          this.finishFlow({ ...data, ...result });
        }
      });
    });
  }

  private finishFlow(finalData?: Partial<SkillsIdeateFlowData>): void {
    const completion$ = this.flowCompletion$;
    this.flowCompletion$ = undefined;
    if (completion$ && !completion$.closed) {
      completion$.next(finalData?.resultJson ?? undefined);
      completion$.complete();
    }
  }
}
